import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { KycStatusService } from '../services/kyc-status.service';
import { gatePassed } from '../utils/compliance-exemption.util';

// Routes the user can still reach when KYC !== APPROVED. Anything outside
// this allow-list bounces back to /dashboard/supplier with a snackbar.
const ALLOWED_PREFIXES = [
  '/dashboard',
  '/settings/kyc',
  '/settings/inbox',
  '/auth',
  '/help',
];

@Injectable({ providedIn: 'root' })
export class KycAccessGuard implements CanActivate, CanActivateChild {
  constructor(
    private kycStatus: KycStatusService,
    private cookies: CookieService,
    private router: Router,
    private snack: MatSnackBar
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.evaluate(state.url || '');
  }

  canActivateChild(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.evaluate(state.url || '');
  }

  private async evaluate(url: string): Promise<boolean> {
    // Admins bypass entirely.
    const role = (this.cookies.get('userRole') || '').trim().toLowerCase();
    if (role === 'admin') {
      return true;
    }

    // Only suppliers ('Suppliers') and buyers ('Owner') are gated. Anyone else
    // (e.g. legacy roles) passes through without interference.
    if (role !== 'suppliers' && role !== 'owner') {
      return true;
    }

    const path = url.split('?')[0];
    if (this.isAllowed(path)) {
      return true;
    }

    // Make sure we have an up-to-date status before deciding.
    let snap = this.kycStatus.snapshot;
    if (snap.loadState !== 'SUCCESS' || !snap.fetchedAt) {
      snap = await this.kycStatus.refresh();
    }

    if (snap.loadState !== 'SUCCESS') {
      return false;
    }

    if (gatePassed(snap)) {
      return true;
    }

    this.snack.open(
      'Complete your KYC verification to access this section.',
      'OK',
      { duration: 4000 }
    );
    this.router.navigateByUrl('/dashboard/supplier');
    return false;
  }

  private isAllowed(path: string): boolean {
    return ALLOWED_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(prefix + '/') || path === prefix
    );
  }
}
